import { BreakpointValue } from "types/generic/types";
import { MediaQueriesProps } from "configs/constants";
import React, { createContext, useContext, useEffect, useState } from "react";

interface BreakpointProviderProps {
  children: React.ReactNode;
  queries: MediaQueriesProps;
}

interface BreakpointKeyQuery {
  [key: string]: boolean;
}

interface MediaQueryLists {
  xs: MediaQueryList;
  sm: MediaQueryList;
  md: MediaQueryList;
  lg: MediaQueryList;
  xl: MediaQueryList;
}

const defaultValue = {};

const BreakpointContext = createContext(defaultValue);

const BreakpointProvider = ({ children, queries }: BreakpointProviderProps) => {
  const [queryMatch, setQueryMatch] = useState({});

  useEffect(() => {
    const mediaQueryLists = {} as MediaQueryLists;
    const keys = Object.keys(queries);
    let isAttached = false;

    const handleQueryListener = () => {
      const updatedMatches = keys.reduce((acc: BreakpointKeyQuery, media) => {
        const currentMedia = media as BreakpointValue;

        acc[currentMedia] = !!(
          mediaQueryLists[currentMedia] && mediaQueryLists[currentMedia].matches
        );
        return acc;
      }, {});
      setQueryMatch(updatedMatches);
    };

    if (window && window.matchMedia) {
      const matches: BreakpointKeyQuery = {};
      keys.forEach((media) => {
        const currentMedia = media as BreakpointValue;
        if (typeof queries[currentMedia] === "string") {
          mediaQueryLists[currentMedia] = window.matchMedia(
            queries[currentMedia],
          );

          matches[currentMedia] = mediaQueryLists[currentMedia].matches;
          mediaQueryLists[currentMedia].addEventListener(
            "change",
            handleQueryListener,
          );
        } else {
          matches[media] = false;
        }
      });
      setQueryMatch(matches);
      isAttached = true;

      // TODO: check this for addListener now moved on up
      // keys.forEach((media) => {
      //   if (typeof queries[media] === "string") {
      //     // @ts-expect-error
      //     mediaQueryLists[media].addListener(handleQueryListener);
      //   }
      // });
    }

    return () => {
      if (isAttached) {
        keys.forEach((media) => {
          const currentMedia = media as BreakpointValue;
          if (typeof queries[currentMedia] === "string") {
            mediaQueryLists[currentMedia].removeEventListener(
              "change",
              handleQueryListener,
            );
          }
        });
      }
    };
  }, [queries]);

  return (
    <BreakpointContext.Provider value={queryMatch}>
      {children}
    </BreakpointContext.Provider>
  );
};

function useBreakpoint() {
  const context = useContext(BreakpointContext);
  if (context === defaultValue) {
    throw new Error("useBreakpoint must be used within BreakpointProvider");
  }
  return context;
}

function insideBreakpoint(
  allBreakpoints: BreakpointKeyQuery,
  validBreakpoints: BreakpointValue[],
  queryMin: boolean = true,
) {
  const result = Object.keys(allBreakpoints).some(
    (key) =>
      allBreakpoints[key] === true &&
      validBreakpoints.indexOf(key as BreakpointValue) >= 0,
  );
  return queryMin === true ? result : !result;
}

export { BreakpointProvider, insideBreakpoint, useBreakpoint };
